import { LinearProgress } from '@mui/joy';

import { ReactFlowProvider } from 'reactflow';

import Banner from '@/components/Banner';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';

import { useGraphAssistant } from './src/api';
import { useGraphAssistantDebugGraph } from './src/api/useGraphSwr';
import { GraphDebugger } from './src/components/debug/GraphDebugger';
import { GraphDatasetProvider } from './src/components/debug/dataset/GraphDatasetProvider';
import { GraphDataContext } from './src/control/graph-data';
import { useLangSmithTracingEnabled } from './src/hooks/useLangSmithTracingEnabled';
import { useAssistantStore } from './src/store/assistants/assistantsStore';

function StatefulThreadPage(props: { assistantId: string }) {
  const assistantId = useAssistantStore((state) => state.activeAssistantId);

  const graph = useGraphAssistantDebugGraph(assistantId);

  return (
    <ReactFlowProvider>
      <GraphDataContext.Provider value={graph.data}>
        <GraphDatasetProvider>
          <GraphDebugger
            assistantId={assistantId ?? props.assistantId}
            graph={graph}
          />
        </GraphDatasetProvider>
      </GraphDataContext.Provider>
    </ReactFlowProvider>
  );
}

export const GraphThreadPage = () => {
  const { showLangSmithKeyWarning } = useLangSmithTracingEnabled();

  const assistantId = useAssistantStore((state) => state.activeAssistantId);
  const {
    data: assistant,
    error: assistantError,
    isLoading: assistantLoading,
  } = useGraphAssistant(assistantId, {
    keepPreviousData: true,
  });

  if (assistantError) {
    return (
      <ExpandableErrorAlert
        error={
          'Failed to fetch assistant. Please try selecting a different assistant and make sure the API server is running.'
        }
      />
    );
  }

  if (assistantLoading && !assistant) {
    return (
      <div className="flex w-full items-start justify-center">
        <LinearProgress />
      </div>
    );
  }

  if (!assistant) return null;

  return (
    <div className="flex h-full w-full flex-col">
      {showLangSmithKeyWarning && (
        <Banner
          title="Not seeing LangSmith runs?"
          description={
            <span className="text-sm text-primary">
              It looks like your LangSmith API key is missing. Please make sure
              to add{' '}
              <span className="font-mono text-xs font-bold">
                LANGSMITH_API_KEY
              </span>{' '}
              to your local server's{' '}
              <span className="font-mono text-xs font-bold">.env</span> file.
            </span>
          }
          closeable
          open
          shadow={false}
          className="mx-4"
        />
      )}
      <StatefulThreadPage assistantId={assistant.assistant_id} />
    </div>
  );
};
